export type SessionTimeSlot = {
  sessionName: string;
  startTime?: string;
  endTime?: string;
};

function timeToMinutes(raw?: string): number {
  const s = String(raw ?? "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return NaN;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return NaN;
  return h * 60 + min;
}

/** Picks the clinic session that matches the current clock time. */
export function resolveSessionForCurrentTime(
  sessions: SessionTimeSlot[],
  now: Date = new Date()
): string {
  const nowM = now.getHours() * 60 + now.getMinutes();

  const slots = sessions
    .map((session) => {
      const sessionName = String(session.sessionName ?? "").trim();
      const startM = timeToMinutes(session.startTime);
      const endM = timeToMinutes(session.endTime);
      return { sessionName, startM, endM };
    })
    .filter(
      (slot) =>
        slot.sessionName &&
        Number.isFinite(slot.startM) &&
        Number.isFinite(slot.endM)
    )
    .sort((a, b) => a.startM - b.startM);

  if (slots.length === 0) return "";

  for (const slot of slots) {
    if (nowM >= slot.startM && nowM < slot.endM) {
      return slot.sessionName;
    }
  }

  if (nowM < slots[0].startM) {
    return slots[0].sessionName;
  }

  return slots[slots.length - 1].sessionName;
}

export async function fetchDefaultSessionForToday(
  api: { get: (url: string, config?: { params?: Record<string, string> }) => Promise<{ data?: { data?: { sessions?: SessionTimeSlot[] } } }> },
  dayName: string,
  availableNames?: string[]
): Promise<string> {
  try {
    const response = await api.get(
      "/client/setting/dentistGeneralHours/getGeneralSessionNames",
      { params: { day: dayName } }
    );
    let sessions: SessionTimeSlot[] = response.data?.data?.sessions ?? [];
    if (availableNames?.length) {
      const allowed = new Set(
        availableNames.map((name) => String(name).trim().toLowerCase()).filter(Boolean)
      );
      sessions = sessions.filter((session) =>
        allowed.has(String(session.sessionName ?? "").trim().toLowerCase())
      );
    }
    return resolveSessionForCurrentTime(sessions);
  } catch {
    return "";
  }
}
